import {
  routeSeoSeed,
  type RouteCategory,
  type RouteSchemaKind
} from '@/config/route-seo-seed';

export type RouteDefinition = {
  key: string;
  path: string;
  title: string;
  navLabel?: string;
  description: string;
  canonicalPath: string;
  legacyPaths: string[];
  category: RouteCategory;
  socialImage: string;
  schemaKinds: RouteSchemaKind[];
  hasLongFormContent: boolean;
  isLegacyAlias: boolean;
};

type CanonicalRouteSeed = {
  path: string;
  title: string;
  navLabel?: string;
  description: string;
  category: RouteCategory;
  legacyPaths: string[];
  socialImage: string;
  schemaKinds: RouteSchemaKind[];
  hasLongFormContent: boolean;
};

const canonicalRouteSeed = routeSeoSeed as CanonicalRouteSeed[];

function createCanonicalRoute(route: CanonicalRouteSeed): RouteDefinition {
  return {
    key: route.path,
    path: route.path,
    title: route.title,
    navLabel: route.navLabel,
    description: route.description,
    canonicalPath: route.path,
    legacyPaths: route.legacyPaths,
    category: route.category,
    socialImage: route.socialImage,
    schemaKinds: route.schemaKinds,
    hasLongFormContent: route.hasLongFormContent,
    isLegacyAlias: false
  };
}

function expandRoute(route: CanonicalRouteSeed): RouteDefinition[] {
  const canonicalRoute = createCanonicalRoute(route);
  const aliasRoutes: RouteDefinition[] = route.legacyPaths.map((legacyPath) => ({
    ...canonicalRoute,
    key: `${canonicalRoute.path}:${legacyPath}`,
    path: legacyPath,
    isLegacyAlias: true
  }));

  return [canonicalRoute, ...aliasRoutes];
}

const canonicalRoutes = canonicalRouteSeed.map((route) => createCanonicalRoute(route));

export const calculatorRoutes: RouteDefinition[] = canonicalRoutes.filter((route) => route.category === 'calculator');

export const contentRoutes: RouteDefinition[] = canonicalRoutes.filter((route) => route.category === 'content');

export const routes: RouteDefinition[] = canonicalRouteSeed.flatMap((route) => expandRoute(route));

export const staticContentLinks = {
  guides: [
    { href: '/guides/', label: 'All Guides' },
    { href: '/guides/first-time-homebuyer/', label: 'First-Time Homebuyer Guide' },
    { href: '/guides/investing-basics/', label: 'Investing Basics' },
    { href: '/guides/debt-payoff-strategies/', label: 'Debt Payoff Strategies' },
    { href: '/guides/fire-number-worked-example/', label: 'FIRE Number Worked Example' },
    { href: '/guides/choosing-a-safe-withdrawal-rate/', label: 'Choosing a Safe Withdrawal Rate' }
  ],
  about: [
    { href: '/about-us/', label: 'About' },
    { href: '/methodology/', label: 'Methodology' },
    { href: '/faq/', label: 'FAQ' },
    { href: '/contact/', label: 'Contact' }
  ],
  resources: [
    { href: '/glossary/', label: 'Glossary' },
    { href: '/changelog/', label: 'Changelog' }
  ],
  legal: [
    { href: '/privacy-policy/', label: 'Privacy Policy' },
    { href: '/terms-of-service/', label: 'Terms of Service' }
  ]
};
